import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

// 2025 Canadian Federal Tax Brackets
const federalTaxBrackets = [
  { limit: 55867, rate: 0.15 },
  { limit: 111733, rate: 0.205 },
  { limit: 173205, rate: 0.26 },
  { limit: 246752, rate: 0.29 },
  { limit: Infinity, rate: 0.33 },
];

// 2025 Provincial Tax Rates (simplified marginal rates)
const provincialTaxRates = {
  'ON': { name: 'Ontario', brackets: [{ limit: 51446, rate: 0.0505 }, { limit: 102894, rate: 0.0915 }, { limit: 150000, rate: 0.1116 }, { limit: 220000, rate: 0.1216 }, { limit: Infinity, rate: 0.1316 }] },
  'BC': { name: 'British Columbia', brackets: [{ limit: 47937, rate: 0.0506 }, { limit: 95875, rate: 0.077 }, { limit: 110076, rate: 0.105 }, { limit: 133664, rate: 0.1229 }, { limit: 181232, rate: 0.147 }, { limit: Infinity, rate: 0.168 }] },
  'AB': { name: 'Alberta', brackets: [{ limit: 148269, rate: 0.10 }, { limit: 177922, rate: 0.12 }, { limit: 237230, rate: 0.13 }, { limit: 355845, rate: 0.14 }, { limit: Infinity, rate: 0.15 }] },
  'SK': { name: 'Saskatchewan', brackets: [{ limit: 52057, rate: 0.105 }, { limit: 148734, rate: 0.125 }, { limit: Infinity, rate: 0.145 }] },
  'MB': { name: 'Manitoba', brackets: [{ limit: 47000, rate: 0.108 }, { limit: 100000, rate: 0.1275 }, { limit: Infinity, rate: 0.174 }] },
  'QC': { name: 'Quebec', brackets: [{ limit: 51780, rate: 0.14 }, { limit: 103545, rate: 0.19 }, { limit: 126000, rate: 0.24 }, { limit: Infinity, rate: 0.2575 }] },
  'NB': { name: 'New Brunswick', brackets: [{ limit: 49958, rate: 0.094 }, { limit: 99916, rate: 0.14 }, { limit: 185064, rate: 0.16 }, { limit: Infinity, rate: 0.195 }] },
  'NS': { name: 'Nova Scotia', brackets: [{ limit: 29590, rate: 0.0879 }, { limit: 59180, rate: 0.1495 }, { limit: 93000, rate: 0.1667 }, { limit: 150000, rate: 0.175 }, { limit: Infinity, rate: 0.21 }] },
  'PE': { name: 'Prince Edward Island', brackets: [{ limit: 32656, rate: 0.098 }, { limit: 64313, rate: 0.138 }, { limit: Infinity, rate: 0.167 }] },
  'NL': { name: 'Newfoundland and Labrador', brackets: [{ limit: 43198, rate: 0.087 }, { limit: 86395, rate: 0.145 }, { limit: 154244, rate: 0.158 }, { limit: 215943, rate: 0.178 }, { limit: Infinity, rate: 0.208 }] },
  'YT': { name: 'Yukon', brackets: [{ limit: 55867, rate: 0.064 }, { limit: 111733, rate: 0.09 }, { limit: 173205, rate: 0.109 }, { limit: 500000, rate: 0.128 }, { limit: Infinity, rate: 0.15 }] },
  'NT': { name: 'Northwest Territories', brackets: [{ limit: 50597, rate: 0.059 }, { limit: 101198, rate: 0.086 }, { limit: 164525, rate: 0.122 }, { limit: Infinity, rate: 0.1405 }] },
  'NU': { name: 'Nunavut', brackets: [{ limit: 53268, rate: 0.04 }, { limit: 106537, rate: 0.07 }, { limit: 173205, rate: 0.09 }, { limit: Infinity, rate: 0.115 }] },
};

const calculateTax = (income, brackets) => {
  let tax = 0;
  let previousLimit = 0;

  for (const bracket of brackets) {
    if (income > previousLimit) {
      const taxableInThisBracket = Math.min(income, bracket.limit) - previousLimit;
      tax += taxableInThisBracket * bracket.rate;
      previousLimit = bracket.limit;
    } else {
      break;
    }
  }

  return tax;
};

const TaxCalculator = () => {
  const [province, setProvince] = useState('');
  const [employmentIncome, setEmploymentIncome] = useState('');
  const [taxDeducted, setTaxDeducted] = useState('');
  const [cppDeducted, setCppDeducted] = useState('');
  const [eiDeducted, setEiDeducted] = useState('');
  const [result, setResult] = useState(null);

  const handleCalculate = () => {
    const income = parseFloat(employmentIncome) || 0;
    const taxPaid = parseFloat(taxDeducted) || 0;
    const cpp = parseFloat(cppDeducted) || 0;
    const ei = parseFloat(eiDeducted) || 0;

    if (!province || income === 0) {
      alert('Please fill in all required fields');
      return;
    }

    // Calculate federal tax
    const federalTax = calculateTax(income, federalTaxBrackets);

    // Calculate provincial tax
    const provincialTax = calculateTax(income, provincialTaxRates[province].brackets);

    // Total tax owing
    const totalTaxOwing = federalTax + provincialTax;

    // Total deductions made
    const totalDeductions = taxPaid + cpp + ei;

    // Refund or owing
    const difference = totalDeductions - totalTaxOwing;

    setResult({
      federalTax: federalTax.toFixed(2),
      provincialTax: provincialTax.toFixed(2),
      totalTaxOwing: totalTaxOwing.toFixed(2),
      totalDeductions: totalDeductions.toFixed(2),
      difference: difference.toFixed(2),
      isRefund: difference > 0,
    });
  };

  const handleReset = () => {
    setProvince('');
    setEmploymentIncome('');
    setTaxDeducted('');
    setCppDeducted('');
    setEiDeducted('');
    setResult(null);
  };

  return (
    <Card className="w-full max-w-3xl mx-auto shadow-lg">
      <CardHeader className="bg-blue-50">
        <div className="flex items-center space-x-3">
          <Calculator className="h-8 w-8 text-blue-600" />
          <div>
            <CardTitle className="text-2xl text-blue-900">Canadian Tax Refund Calculator</CardTitle>
            <CardDescription>Calculate your estimated tax refund or amount owing for 2025</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Province Selection */}
          <div className="space-y-2">
            <Label htmlFor="province">Province/Territory *</Label>
            <Select value={province} onValueChange={setProvince}>
              <SelectTrigger>
                <SelectValue placeholder="Select your province" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(provincialTaxRates).map(([code, data]) => (
                  <SelectItem key={code} value={code}>
                    {data.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Employment Income */}
          <div className="space-y-2">
            <Label htmlFor="income">Employment Income *</Label>
            <Input
              id="income"
              type="number"
              placeholder="Enter your employment income"
              value={employmentIncome}
              onChange={(e) => setEmploymentIncome(e.target.value)}
            />
          </div>

          {/* Tax Deducted */}
          <div className="space-y-2">
            <Label htmlFor="taxDeducted">Tax Deducted *</Label>
            <Input
              id="taxDeducted"
              type="number"
              placeholder="Enter tax deducted from pay"
              value={taxDeducted}
              onChange={(e) => setTaxDeducted(e.target.value)}
            />
          </div>

          {/* CPP Deducted */}
          <div className="space-y-2">
            <Label htmlFor="cpp">CPP Deducted *</Label>
            <Input
              id="cpp"
              type="number"
              placeholder="Enter CPP contributions"
              value={cppDeducted}
              onChange={(e) => setCppDeducted(e.target.value)}
            />
          </div>

          {/* EI Deducted */}
          <div className="space-y-2">
            <Label htmlFor="ei">EI Deducted *</Label>
            <Input
              id="ei"
              type="number"
              placeholder="Enter EI contributions"
              value={eiDeducted}
              onChange={(e) => setEiDeducted(e.target.value)}
            />
          </div>

          {/* Buttons */}
          <div className="flex space-x-3">
            <Button onClick={handleCalculate} className="bg-blue-600 hover:bg-blue-700 flex-1">
              Calculate
            </Button>
            <Button onClick={handleReset} variant="outline" className="flex-1">
              Reset
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="mt-6 p-6 bg-slate-50 rounded-lg border-2 border-blue-200">
              <h3 className="text-lg font-semibold mb-4 text-blue-900">Calculation Results</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-700">Federal Tax:</span>
                  <span className="font-semibold">${result.federalTax}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Provincial Tax:</span>
                  <span className="font-semibold">${result.provincialTax}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-700 font-medium">Total Tax Owing:</span>
                  <span className="font-semibold">${result.totalTaxOwing}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Total Deductions Paid:</span>
                  <span className="font-semibold">${result.totalDeductions}</span>
                </div>
                <div className={`flex justify-between border-t-2 pt-3 ${result.isRefund ? 'text-green-700' : 'text-red-700'}`}>
                  <span className="font-bold text-lg">
                    {result.isRefund ? 'Estimated Refund:' : 'Amount Owing:'}
                  </span>
                  <span className="font-bold text-xl">
                    ${Math.abs(result.difference)}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                * This is an estimate based on 2025 tax rates. Actual refund may vary based on deductions, credits, and other factors.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TaxCalculator;
